<template>
  <div>
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="modelValue"
        class="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-xs z-50 pointer-events-auto"
        @click="close"
      />
    </Transition>

    <Transition
      enter-active-class="transition duration-400 cubic-bezier(0.16, 1, 0.3, 1)"
      enter-from-class="translate-y-full lg:translate-y-[-16px] lg:scale-95 lg:opacity-0"
      enter-to-class="translate-y-0 lg:translate-y-0 lg:scale-100 lg:opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="translate-y-0 lg:translate-y-0 lg:scale-100 lg:opacity-100"
      leave-to-class="translate-y-full lg:translate-y-[-8px] lg:scale-95 lg:opacity-0"
    >
      <div
        v-if="modelValue"
        ref="dropdownRef"
        class="fixed bottom-0 inset-x-0 w-full max-h-[85vh] lg:max-h-none lg:absolute lg:top-14 lg:bottom-auto lg:right-4 lg:left-auto lg:w-80 z-60 pointer-events-auto flex flex-col"
        :class="{ 'pointer-events-none': isSelecting }"
      >
        <div
          class="lg:hidden w-12 h-1 bg-white/20 rounded-full mx-auto mb-2 shrink-0"
        />

        <div
          class="flex flex-col overflow-y-auto lg:overflow-hidden bg-[#0f0f11]/95 lg:bg-background-dark/70 backdrop-blur-xl lg:backdrop-blur-sm border-t border-x border-white/10 lg:border rounded-t-3xl lg:rounded-2xl shadow-2xl shadow-black/80 p-3 pb-8 lg:pb-3 max-h-full"
        >
          <div
            v-if="isSelecting"
            class="absolute inset-0 z-50 bg-[#2b1a12]/75 backdrop-blur-[2px] flex items-center justify-center transition-all duration-300 rounded-t-3xl lg:rounded-2xl"
          >
            <div class="flex flex-col items-center gap-2">
              <Loader2 class="text-brand animate-spin" :size="24" />
              <span
                class="text-[10px] font-black text-brand tracking-[0.2em] uppercase"
                >Switching Profile</span
              >
            </div>
          </div>

          <div class="p-4 flex items-center gap-4 border-b border-white/5 mb-2">
            <div class="relative shrink-0">
              <div
                class="w-14 h-14 rounded-full border-2 border-brand p-0.5 shadow-[0_0_15px_rgba(255,95,0,0.2)]"
              >
                <img
                  v-if="user?.active_profile?.avatar?.url"
                  :src="user?.active_profile?.avatar?.url"
                  class="w-full h-full rounded-full bg-overlay-dark object-cover"
                  alt="Profile Avatar"
                />
                <div
                  v-else
                  class="w-full h-full rounded-full flex items-center justify-center text-[11px] text-white font-black bg-linear-to-br relative overflow-hidden"
                  :class="getRandomGradient(activeProfile?.id || 0)"
                >
                  <div
                    class="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.14),transparent_60%)] pointer-events-none"
                  />
                  <span class="relative z-10">{{
                    getInitials(activeProfile?.name)
                  }}</span>
                </div>
              </div>
              <div
                class="absolute -bottom-0.5 -right-0.5 w-4.5 h-4.5 bg-brand rounded-full border-2 border-[#0f0f11] lg:border-background-dark flex items-center justify-center"
              >
                <div class="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              </div>
            </div>

            <div class="overflow-hidden">
              <h4 class="text-white font-black leading-tight truncate">
                {{ activeProfile?.name }}
              </h4>
              <p
                class="text-[9px] font-black text-brand tracking-widest uppercase mt-0.5"
              >
                Active Profile
              </p>
            </div>
          </div>

          <div class="px-4 py-3">
            <p
              class="text-[10px] font-black text-white/30 tracking-widest mb-3 uppercase"
            >
              Switch Profile
            </p>

            <div class="flex flex-wrap items-center gap-3.5">
              <button
                v-for="(profile, index) in otherProfiles"
                :key="profile.id"
                class="relative size-11 rounded-full border border-white/10 hover:border-brand/50 transition-all overflow-hidden grayscale hover:grayscale-0 group cursor-pointer active:scale-95 shrink-0"
                :disabled="isSelecting"
                @click="selectProfile(profile)"
              >
                <div
                  v-if="pendingProfileId === profile.id"
                  class="absolute inset-0 bg-black/60 flex items-center justify-center z-10"
                >
                  <Loader2 :size="14" class="text-brand animate-spin" />
                </div>

                <img
                  v-if="profile?.avatar?.url"
                  class="w-full h-full rounded-full bg-overlay-dark object-cover"
                  :src="profile?.avatar?.url"
                  alt="Avatar option"
                />
                <div
                  v-else
                  class="w-full h-full rounded-full flex items-center justify-center text-[10px] text-white font-black bg-linear-to-br relative"
                  :class="getRandomGradient(profile.id || index)"
                >
                  <div
                    class="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.1),transparent_60%)] pointer-events-none"
                  />
                  <span class="relative z-10">{{
                    getInitials(profile.name)
                  }}</span>
                </div>
              </button>

              <button
                class="size-11 rounded-full border border-dashed border-white/20 flex items-center justify-center text-white/40 hover:text-brand hover:border-brand/40 bg-white/2 transition-all cursor-pointer active:scale-95 shrink-0"
                @click="handleProfileManagementNavigation"
              >
                <Plus :size="18" />
              </button>
            </div>
          </div>

          <nav class="space-y-1 px-1">
            <NuxtLink
              v-for="item in menuItems"
              :key="item.label"
              :to="item.href"
              class="flex items-center justify-between px-4 py-3.5 rounded-xl lg:rounded-full hover:bg-white/5 active:bg-brand/10 transition-all group"
              @click="close"
            >
              <div class="flex items-center gap-4">
                <component
                  :is="item.icon"
                  :size="18"
                  class="text-white/40 group-hover:text-brand transition-colors"
                />
                <span
                  class="text-sm font-semibold text-white/70 group-hover:text-white transition-colors"
                >
                  {{ item.label }}
                </span>
              </div>
              <ChevronRight
                :size="14"
                class="text-white/20 group-hover:text-white group-hover:translate-x-1 transition-all"
              />
            </NuxtLink>
          </nav>

          <div class="mt-auto lg:mt-4 pt-3 border-t border-white/5 px-1">
            <button
              type="button"
              @click="handleLogout"
              class="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl lg:rounded-full text-xs font-black tracking-[0.2em] transition-all group bg-red-500/10 hover:bg-red-500/15 text-red-400 hover:text-red-300 cursor-pointer uppercase"
            >
              <LogOut :size="14" />
              Sign Out Account
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useRuntimeConfig } from "#app";
import {
  Bookmark,
  History,
  LogOut,
  Plus,
  ChevronRight,
  Loader2,
  SquarePlus,
} from "lucide-vue-next";
import { useNuxtServerApi } from "~/composables/http/useNuxtServerAPI";
import { useProfileStore } from "~~/stores/auth/profile";
import useGetUserRole from "~/utils/auth/useGetUserRole";

const props = defineProps<{
  modelValue: boolean;
  ignoreElement?: HTMLElement | null;
}>();
const emit = defineEmits(["update:modelValue"]);

const { logout, user, fetchUser } = useAuth();
const $nuxtApi = useNuxtServerApi();
const { getInitials } = useStringUtils();
const { getCreatorJoinUrl } = useGetPebbleCmsUrls();
const { setActiveProfile, activeProfile } = useProfileStore();

const dropdownRef = ref<HTMLElement | null>(null);
const profiles = ref<any[]>([]);
const isSelecting = ref(false);
const pendingProfileId = ref<number | null>(null);
const ENDPOINT = "viewer/profiles";

const config = useRuntimeConfig();
const landingPageUrl = config.public.landingPageUrl;

const AVATAR_GRADIENTS = [
  "from-indigo-600 to-cyan-500",
  "from-emerald-500 to-teal-700",
  "from-purple-600 to-pink-500",
  "from-amber-500 to-rose-600",
  "from-blue-600 to-violet-600",
  "from-fuchsia-600 to-orange-500",
];

const getRandomGradient = (seed: string | number): string => {
  const hash =
    typeof seed === "string"
      ? seed.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : seed;
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length]!;
};

const otherProfiles = computed(() => {
  const filtered = profiles.value.filter((p) => p.id !== activeProfile?.id);
  return [...filtered].reverse();
});

const isAnyCreator = computed(() => useGetUserRole(user.value).isAnyCreator());

const menuItems = computed(() => [
  { label: "Watchlist", icon: Bookmark, href: "/content/watchlist" },
  { label: "Watch History", icon: History, href: "/content/watch-history" },
  ...(isAnyCreator.value
    ? [
        {
          label: "Creator Studio",
          icon: SquarePlus,
          href: getCreatorJoinUrl,
        },
      ]
    : []),
]);

const close = () => emit("update:modelValue", false);

const handleClickOutside = (event: MouseEvent) => {
  if (
    window.innerWidth >= 1024 &&
    dropdownRef.value &&
    !dropdownRef.value.contains(event.target as Node) &&
    !props.ignoreElement?.contains(event.target as Node)
  ) {
    close();
  }
};

// Fixed watch block containing explicit environment runtime validations
watch(
  () => props.modelValue,
  (isOpen) => {
    // Structural Guard: Ensure code only evaluates inside actual browser execution scopes
    if (typeof window === "undefined" || !process.client) return;

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside, {
        passive: true,
      });
      fetchProfiles();
    } else {
      // Defensive Check: Only drop the listener if document context exists
      if (typeof document !== "undefined") {
        document.removeEventListener("mousedown", handleClickOutside);
      }
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  if (typeof document !== "undefined") {
    document.removeEventListener("mousedown", handleClickOutside);
  }
});

const fetchProfiles = async () => {
  try {
    const response = await $nuxtApi.get<any>(ENDPOINT);
    const rawData = response.data?.data?.data || response.data;
    profiles.value = Array.isArray(rawData) ? rawData : [];
  } catch (error) {
    console.error("Failed to fetch profiles:", error);
  }
};

const selectProfile = async (profile: any) => {
  if (isSelecting.value) return;
  isSelecting.value = true;
  pendingProfileId.value = profile.id;
  try {
    setActiveProfile(profile);
    await fetchUser({ refresh: true });
    close();
    window.location.href = landingPageUrl;
  } catch (error) {
    console.error("Failed to select profile:", error);
  } finally {
    isSelecting.value = false;
    pendingProfileId.value = null;
  }
};

const handleProfileManagementNavigation = () => {
  close();
  navigateTo("/profiles");
};

const handleLogout = () => {
  close();
  logout();
  navigateTo("/");
};
</script>
